import { Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilStateLoadable } from 'recoil';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import SnapshotTabsBase, {
  ISnapshotTab,
} from '../components/Snapshot/SnapshotTabs/SnapshotTabsBase';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((_theme) => ({
  root: {
    padding: '0px',
    '& MuiTabPanel-root': {
      padding: '0px',
    },
  },
  apiUsageContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'scroll',
    paddingTop: '37px',
  },
  perDayUsageWidth: {
    width: '100% !important',
    maxWidth: '30px',
  },
}));

const ApiUsage = () => {
  const classes = useStyles();
  const [userLoadable] = useRecoilStateLoadable(user);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    {
      field: 'usageTotal',
      sort: 'desc',
    },
  ]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch]
  );

  const userContents = userLoadable.contents as IUserAtom;

  const [startDate, setStarting] = useState(
    moment().subtract(5, 'days').format('YYYY-MM-DD')
  );

  const [endDate, setEnding] = useState(moment().format('YYYY-MM-DD'));

  const [value, setValue] = React.useState(1);

  const setDates = (localStart: any, localEnd: any, index: number) => {
    setStarting(localStart);
    setEnding(localEnd);
  };

  const dateSnapshotTabs: ISnapshotTab[] = [
    {
      label: 'Today',
    },
    {
      label: 'Last 5 Days',
    },
    {
      label: 'Last 10 Days',
    },
    {
      label: 'Last 15 Days',
    },
    {
      label: 'Last 30 Days',
    },
  ];

  const timeArr = useMemo(() => {
    const datesObj = {};
    const datesArr: string[] = [];
    let newStart = startDate;
    while (moment(newStart) <= moment(endDate)) {
      datesObj[newStart] = 0;
      datesArr.push(newStart);
      newStart = moment(newStart).add(1, 'days').format('YYYY-MM-DD');
    }
    return [datesObj, datesArr];
  }, [startDate, endDate]);

  const columns = useMemo<IColDef[]>(() => {
    setLoading(true);
    if (timeArr?.length) {
      const dateArr: string[] = [...(timeArr[1] as string[])];
      const updatedCols: IColDef[] = [
        {
          field: 'businessid',
          headerName: 'Business Id',
          headerAlign: 'left',
          width: 150,
        },
        {
          field: 'usageTotal',
          headerName: 'Total',
          headerAlign: 'left',
        },
        {
          field: 'usageAverage',
          headerName: 'Average',
          headerAlign: 'left',
        },
      ];
      dateArr.forEach((newStart, i) => {
        return (
          i !== 0 &&
          updatedCols.push({
            field: newStart,
            headerName: moment(newStart).format('MM/DD'),
            enableSorting: true,
            headerAlign: 'center',
            headerClassName: classes.perDayUsageWidth,
            cellClassName: classes.perDayUsageWidth,
            sortComparator: (a, b) => Number(a) - Number(b),
            renderCell: (params) => {
              return (
                <div
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    color:
                      params.row.data?.[params.field]?.percentageUsed >= 1
                        ? '#f10606'
                        : '#121416',
                  }}
                >
                  {params.value}
                </div>
              );
            },
          } as IColDef)
        );
      });
      setLoading(false);
      return updatedCols;
    } else {
      setLoading(false);
      return [];
    }
  }, [timeArr]);

  const [
    _,
    apiUsageLoadable,
    setRefetchToggleForApiUsage,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_API_USAGE,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_API_USAGE,
        apikey: userContents?.apikey,
        businessId: userContents?.businessid,
        businessid: userContents?.businessid,
        startDate: startDate,
        endDate: endDate,
        sortBusiness: 'asc',
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  useEffect(() => {
    const hasField = columns.some((value) => {
      return value.field == sortModel[0].field;
    });
    setGridSortModel([
      {
        field: hasField ? sortModel[0].field : 'usageTotal',
        sort: hasField ? sortModel[0].sort : 'desc',
      },
    ]);
    setSearch('');
    setLoading(true);
    setRefetchToggleForApiUsage({});
  }, [value]);

  const data = useMemo(
    () =>
      apiUsageLoadable?.state === 'hasValue' &&
      apiUsageLoadable?.contents?.response?.data?.result?.length
        ? apiUsageLoadable?.contents?.response?.data?.result
        : [],
    [apiUsageLoadable]
  );

  const rows = useMemo(() => {
    setLoading(true);
    const transformed: any = [];
    data.forEach((el, i) => {
      const updatedTime: any = { ...timeArr[0] };
      let usageTotal = 0;
      let numValues = 0;
      for (const [key, value] of Object.entries(el)) {
        if (key !== 'businessid' && key !== 'date') {
          numValues++;
          usageTotal += (value as any)?.usedRequests ?? 0;
          updatedTime[key] = (value as any)?.usedRequests ?? 0;
        } else if (key === 'businessid') {
          updatedTime[key] = value;
          updatedTime.id = value;
        }
      }
      updatedTime.usageAverage =
        usageTotal > 0 ? Math.round(usageTotal / numValues) : 0;
      updatedTime.usageTotal = usageTotal;
      transformed.push({ ...updatedTime, data: el });
    });
    setLoading(false);
    return transformed;
  }, [data, timeArr, search]);

  const filtered = useMemo(
    () =>
      search !== ''
        ? rows?.filter((el) => Number(el?.id) === Number(search))
        : rows,
    [rows, search]
  );

  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        search={search}
        setSearch={handleSearchChange}
        isLoading={apiUsageLoadable.state === 'loading' || loading}
        dataType="apiUsage"
      />
    ),
    [apiUsageLoadable, search, loading]
  );

  const handleChange = (_event: any, newValue: any) => {
    switch (newValue) {
      case 0:
        setDates(
          moment().subtract(1, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
          newValue
        );
        break;
      case 1:
        setDates(
          moment().subtract(5, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
          newValue
        );
        break;
      case 2:
        setDates(
          moment().subtract(10, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
          newValue
        );
        break;
      case 3:
        setDates(
          moment().subtract(15, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
          newValue
        );
        break;
      case 4:
        setDates(
          moment().subtract(30, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
          newValue
        );
        break;
    }
    setSearch('');
    setValue(newValue);
  };

  return (
    <Paper className={classes.apiUsageContainer} square={true} elevation={0}>
      <SnapshotTabsBase
        onHandleChange={handleChange}
        value={value}
        tabs={dateSnapshotTabs}
        classes={classes}
      >
        <div style={{ flexGrow: 1 }}>
          <DataTable
            hideLastPageButton={true}
            columns={columns}
            rows={filtered}
            rowCount={filtered?.length ?? 0}
            loading={apiUsageLoadable.state === 'loading' || loading}
            sortModel={sortModel}
            ToolbarComponent={ToolbarComponent}
          />
        </div>
      </SnapshotTabsBase>
    </Paper>
  );
};

export default ApiUsage;
